import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog/ConfirmDialog';
import { AttendeeImport } from '@/components/attendees/AttendeeImport';
import { useNotificationStore } from '@/stores/notificationStore';
import { AttendeesService, type AttendeeDTO } from '@/services/AttendeesService';
import type { RecordWithSourceDTO } from '@/services/FlightManifestService';
import {
	X, Search, Users, Plus, Trash2, Check, Plane, Upload,
} from 'lucide-react';

interface AttendeeManagementModalProps {
	isOpen: boolean;
	onClose: () => void;
	eventId: string;
	eventName?: string;
	/** Flight manifest records for computing flight counts per attendee */
	manifestRecords?: RecordWithSourceDTO[];
	/** Call after attendees change so parent can refresh matching */
	onAttendeesChanged?: () => void;
}

interface EditingState {
	attendeeId: string;
	field: string;
	value: string;
}

const attendeesService = new AttendeesService();

export function AttendeeManagementModal({
	isOpen,
	onClose,
	eventId,
	eventName,
	manifestRecords = [],
	onAttendeesChanged,
}: AttendeeManagementModalProps) {
	const notifySuccess = useNotificationStore(s => s.success);
	const notifyError = useNotificationStore(s => s.error);

	const [loading, setLoading] = useState(true);
	const [attendees, setAttendees] = useState<AttendeeDTO[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [searchQuery, setSearchQuery] = useState('');
	const [editing, setEditing] = useState<EditingState | null>(null);
	const [saving, setSaving] = useState(false);
	const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
	const [addingRow, setAddingRow] = useState(false);
	const [newFirstName, setNewFirstName] = useState('');
	const [newLastName, setNewLastName] = useState('');
	const [importOpen, setImportOpen] = useState(false);

	// Build flight count lookup from manifest records
	const flightCountByAttendee = useMemo(() => {
		const map = new Map<string, number>();
		for (const r of manifestRecords) {
			if (r.matched_attendee_id) {
				const flights = (r as any).all_flights?.length || 0;
				map.set(r.matched_attendee_id, (map.get(r.matched_attendee_id) || 0) + flights);
			}
		}
		return map;
	}, [manifestRecords]);

	const loadAttendees = useCallback(async () => {
		setLoading(true);
		try {
			const PAGE_SIZE = 100;
			const firstPage = await attendeesService.listByEvent(eventId, 1, PAGE_SIZE);
			let allItems = [...firstPage.items];
			const totalPages = firstPage.pagination.totalPages;

			if (totalPages > 1) {
				const remaining = await Promise.all(
					Array.from({ length: totalPages - 1 }, (_, i) =>
						attendeesService.listByEvent(eventId, i + 2, PAGE_SIZE)
					)
				);
				for (const page of remaining) {
					allItems = allItems.concat(page.items);
				}
			}

			setAttendees(allItems);
			setTotalCount(firstPage.pagination.totalItems);
		} catch (e: any) {
			notifyError(e?.message || 'Failed to load attendees');
		} finally {
			setLoading(false);
		}
	}, [eventId]);

	useEffect(() => {
		if (isOpen) {
			loadAttendees();
			setSearchQuery('');
			setEditing(null);
			setAddingRow(false);
		}
	}, [isOpen, loadAttendees]);

	const filteredAttendees = useMemo(() => {
		if (!searchQuery.trim()) return attendees;
		const q = searchQuery.toLowerCase();
		return attendees.filter(a =>
			a.first_name?.toLowerCase().includes(q) ||
			a.last_name?.toLowerCase().includes(q) ||
			a.email?.toLowerCase().includes(q) ||
			a.full_name?.toLowerCase().includes(q) ||
			(a as any).external_ref?.toString().includes(q)
		);
	}, [attendees, searchQuery]);

	// ── Inline editing ──

	const startEdit = (attendeeId: string, field: string, currentValue: string) => {
		setEditing({ attendeeId, field, value: currentValue || '' });
	};

	const cancelEdit = () => setEditing(null);

	const saveEdit = async () => {
		if (!editing) return;
		setSaving(true);
		try {
			await attendeesService.update(editing.attendeeId, {
				[editing.field]: editing.value || undefined,
			});
			setAttendees(prev =>
				prev.map(a =>
					a.id === editing.attendeeId
						? { ...a, [editing.field]: editing.value }
						: a
				)
			);
			setEditing(null);
			onAttendeesChanged?.();
		} catch (e: any) {
			notifyError(e?.message || 'Failed to update');
		} finally {
			setSaving(false);
		}
	};

	const handleEditKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') saveEdit();
		if (e.key === 'Escape') cancelEdit();
	};

	// ── Add new attendee ──

	const addAttendee = async () => {
		if (!newFirstName.trim() || !newLastName.trim()) return;
		setSaving(true);
		try {
			await attendeesService.create(eventId, {
				first_name: newFirstName.trim(),
				last_name: newLastName.trim(),
			});
			setAddingRow(false);
			setNewFirstName('');
			setNewLastName('');
			await loadAttendees();
			onAttendeesChanged?.();
			notifySuccess('Attendee added');
		} catch (e: any) {
			notifyError(e?.message || 'Failed to add attendee');
		} finally {
			setSaving(false);
		}
	};

	// ── Delete attendee ──

	const deleteAttendee = async (id: string) => {
		try {
			await attendeesService.delete(id);
			setAttendees(prev => prev.filter(a => a.id !== id));
			setTotalCount(prev => prev - 1);
			setDeleteConfirm(null);
			onAttendeesChanged?.();
			notifySuccess('Attendee deleted');
		} catch (e: any) {
			notifyError(e?.message || 'Failed to delete attendee');
		}
	};

	// ── Editable cell renderer ──

	const EditableCell = ({ attendee, field, value }: { attendee: AttendeeDTO; field: string; value: string }) => {
		const isEditing = editing?.attendeeId === attendee.id && editing?.field === field;

		if (isEditing) {
			return (
				<input
					autoFocus
					value={editing.value}
					onChange={(e) => setEditing({ ...editing, value: e.target.value })}
					onKeyDown={handleEditKeyDown}
					onBlur={saveEdit}
					className="w-full px-1.5 py-0.5 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
					disabled={saving}
				/>
			);
		}

		return (
			<span
				className="block w-full cursor-pointer hover:bg-gray-100 px-1.5 py-0.5 rounded truncate"
				onClick={() => startEdit(attendee.id, field, value)}
				title={value || 'Click to edit'}
			>
				{value || <span className="text-gray-300">—</span>}
			</span>
		);
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="fullScreen">
			<div className="flex flex-col h-screen">
				{/* Header */}
				<div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-6 py-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-6">
							<h2 className="text-lg font-semibold text-gray-900">Attendee Management</h2>
							<div className="flex items-center gap-4 text-sm">
								{eventName && <span className="font-medium text-gray-700">{eventName}</span>}
								<div className="flex items-center gap-1 text-gray-600">
									<Users className="w-4 h-4" />
									<span>{totalCount} attendees</span>
								</div>
							</div>
						</div>
						<button
							onClick={onClose}
							className="rounded-md p-1.5 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
							aria-label="Close modal"
						>
							<X className="w-5 h-5 text-gray-500" />
						</button>
					</div>
				</div>

				{/* Toolbar */}
				<div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-3">
					<div className="flex items-center gap-3">
						<div className="flex-1 max-w-md">
							<Input
								placeholder="Search attendees..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								leftIcon={<Search className="w-4 h-4" />}
							/>
						</div>
						<Button variant="outline" size="sm" onClick={() => { setAddingRow(true); }} className="gap-1.5">
							<Plus className="w-4 h-4" /> Add Attendee
						</Button>
						<Button variant="outline" size="sm" onClick={() => setImportOpen(true)} className="gap-1.5">
							<Upload className="w-4 h-4" /> Import CSV
						</Button>
					</div>
				</div>

				{/* Content */}
				<div className="flex-1 overflow-auto px-6 py-4">
					{loading ? (
						<div className="flex items-center justify-center py-20">
							<Spinner size="lg" />
						</div>
					) : attendees.length === 0 ? (
						<div className="text-center py-20">
							<div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
								<Users className="w-8 h-8 text-gray-400" />
							</div>
							<h3 className="text-lg font-semibold text-gray-900 mb-2">No Attendees Yet</h3>
							<p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
								Add attendees manually or import from a CSV file.
							</p>
							<Button onClick={() => setAddingRow(true)} className="gap-2">
								<Plus className="w-4 h-4" /> Add First Attendee
							</Button>
						</div>
					) : (
						<table className="w-full text-sm table-fixed">
							<colgroup>
								<col style={{ width: '40px' }} />
								<col style={{ width: '18%' }} />
								<col style={{ width: '18%' }} />
								<col style={{ width: '22%' }} />
								<col style={{ width: '14%' }} />
								<col style={{ width: '12%' }} />
								<col style={{ width: '8%' }} />
								<col style={{ width: '40px' }} />
							</colgroup>
							<thead>
								<tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									<th className="pb-2 pr-2">#</th>
									<th className="pb-2 pr-2">First Name</th>
									<th className="pb-2 pr-2">Last Name</th>
									<th className="pb-2 pr-2">Email</th>
									<th className="pb-2 pr-2">Phone</th>
									<th className="pb-2 pr-2">Ref</th>
									<th className="pb-2 pr-2">
										<span className="flex items-center gap-1"><Plane className="w-3 h-3" /> Flights</span>
									</th>
									<th className="pb-2"></th>
								</tr>
							</thead>
							<tbody>
								{/* Add new row */}
								{addingRow && (
									<tr className="border-b border-blue-100 bg-blue-50/30">
										<td className="py-2 pr-2 text-gray-400">+</td>
										<td className="py-2 pr-2">
											<input
												autoFocus
												placeholder="First name"
												value={newFirstName}
												onChange={(e) => setNewFirstName(e.target.value)}
												onKeyDown={(e) => { if (e.key === 'Enter') addAttendee(); if (e.key === 'Escape') { setAddingRow(false); setNewFirstName(''); setNewLastName(''); } }}
												className="w-full px-1.5 py-0.5 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
											/>
										</td>
										<td className="py-2 pr-2">
											<input
												placeholder="Last name"
												value={newLastName}
												onChange={(e) => setNewLastName(e.target.value)}
												onKeyDown={(e) => { if (e.key === 'Enter') addAttendee(); if (e.key === 'Escape') { setAddingRow(false); setNewFirstName(''); setNewLastName(''); } }}
												className="w-full px-1.5 py-0.5 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
											/>
										</td>
										<td className="py-2 pr-2 text-gray-300">—</td>
										<td className="py-2 pr-2 text-gray-300">—</td>
										<td className="py-2 pr-2 text-gray-300">—</td>
										<td className="py-2 pr-2 text-gray-300">—</td>
										<td className="py-2">
											<div className="flex items-center gap-1">
												<button onClick={addAttendee} disabled={saving || !newFirstName.trim() || !newLastName.trim()} className="p-0.5 text-green-600 hover:text-green-700 disabled:opacity-40">
													<Check className="w-4 h-4" />
												</button>
												<button onClick={() => { setAddingRow(false); setNewFirstName(''); setNewLastName(''); }} className="p-0.5 text-gray-400 hover:text-gray-600">
													<X className="w-4 h-4" />
												</button>
											</div>
										</td>
									</tr>
								)}

								{filteredAttendees.map((a, i) => {
									const flightCount = flightCountByAttendee.get(a.id) || 0;
									return (
										<tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50/50">
											<td className="py-1.5 pr-2 text-gray-400 text-xs">{i + 1}</td>
											<td className="py-1.5 pr-2">
												<EditableCell attendee={a} field="first_name" value={a.first_name} />
											</td>
											<td className="py-1.5 pr-2">
												<EditableCell attendee={a} field="last_name" value={a.last_name} />
											</td>
											<td className="py-1.5 pr-2">
												<EditableCell attendee={a} field="email" value={a.email || ''} />
											</td>
											<td className="py-1.5 pr-2">
												<EditableCell attendee={a} field="phone" value={a.phone || ''} />
											</td>
											<td className="py-1.5 pr-2">
												<EditableCell attendee={a} field="external_ref" value={(a as any).external_ref || ''} />
											</td>
											<td className="py-1.5 pr-2 text-xs">
												{flightCount > 0 ? (
													<span className="text-green-700 font-medium">{flightCount}</span>
												) : (
													<span className="text-gray-300">0</span>
												)}
											</td>
											<td className="py-1.5">
												<button
													onClick={() => setDeleteConfirm(a.id)}
													className="p-1 text-gray-400 hover:text-red-500 transition-colors"
													title="Delete attendee"
												>
													<Trash2 className="w-3.5 h-3.5" />
												</button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					)}
				</div>
			</div>

			{/* Delete confirmation */}
			<ConfirmDialog
				isOpen={!!deleteConfirm}
				title="Delete Attendee"
				description="Are you sure you want to delete this attendee? This cannot be undone."
				confirmText="Delete"
				variant="danger"
				onConfirm={() => deleteConfirm && deleteAttendee(deleteConfirm)}
				onClose={() => setDeleteConfirm(null)}
			/>

			{/* CSV Import modal */}
			<AttendeeImport
				eventId={eventId}
				isOpen={importOpen}
				onClose={() => {
					setImportOpen(false);
					loadAttendees();
					onAttendeesChanged?.();
				}}
				onImportComplete={() => {
					// Just refresh data in the background; user will click "Done" to close
					loadAttendees();
					onAttendeesChanged?.();
				}}
			/>
		</Modal>
	);
}
